import { InjectVuexModule, InjectModule } from 'vue-di/vuex';
import { Mutation, Action } from 'vuex-module-decorators';
import { EdgeSiteAccountApi, EdgeAuthenticationApi } from '@trialcard/apigateway.client';
import {
    SiteAccountRequestModel,
    EnterpriseCoordinatorSiteAccountUpsearchResponseModel,
    UserRequestModel,
} from '@trialcard/apigateway.models';
import { Resolve } from 'vue-di';
import { OnClear } from '~/services/clearModule';
import { clearRecords } from '~/services/clearRecords';
import CommonTools from '~/components/tools/commonTools';

@InjectModule({ stateFactory: true }, module)
@OnClear<RegisterModule>(async value => value.clear())
export default class RegisterModule extends InjectVuexModule {
    @Resolve
    siteAccountApi!: EdgeSiteAccountApi;
    @Resolve
    authenticationApi!: EdgeAuthenticationApi;

    siteAccount = {
        practiceName: '',
        addressOne: '',
        addressTwo: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        fax: '',
    };

    siteAdministrator = {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        fax: '',
    };

    @Mutation
    updateSiteAccount(data: typeof RegisterModule.prototype.siteAccount) {
        Object.assign(this.siteAccount, data);
    }

    @Mutation
    updateSiteAdministrator(data: typeof RegisterModule.prototype.siteAdministrator) {
        Object.assign(this.siteAdministrator, data);
    }

    @Mutation
    clear() {
        clearRecords(this.siteAccount);
        clearRecords(this.siteAdministrator);
    }

    @Action({})
    public async siteRegistration() {
        const accountExistsResponse = await this.adminAccountExists();
        if (!accountExistsResponse || accountExistsResponse.accountExists) return false; // This should cover instances when the call returns an error, or a successful call returns the account already exists.
        const siteAccount = await this.createSiteAccount();
        if (!siteAccount) return false;
        const siteAdminAuthentication = await this.createAdminCredentials(siteAccount.accountId!);
        return !!siteAccount && !!siteAdminAuthentication;
    }

    @Action({})
    private async createSiteAccount() {
        try {
            const request = {
                siteAccount: {
                    siteName: this.siteAccount.practiceName,
                    addressOne: this.siteAccount.addressOne,
                    addressTwo: this.siteAccount.addressTwo ? this.siteAccount.addressTwo : undefined,
                    city: this.siteAccount.city,
                    state: this.siteAccount.state,
                    zip: this.siteAccount.zip,
                    phoneNumber: this.siteAccount.phone && this.siteAccount.phone.replace(/\D/g, ''),
                    phoneNumberExtension: '',
                    fax: this.siteAccount.fax && this.siteAccount.fax.replace(/\D/g, ''),
                },
                siteAccountContact: {
                    firstName: this.siteAdministrator.firstName,
                    lastName: this.siteAdministrator.lastName,
                    email: this.siteAdministrator.email,
                    phoneNumber: this.siteAdministrator.phone && this.siteAdministrator.phone.replace(/\D/g, ''),
                    phoneNumberExtension: '',
                    fax: this.siteAccount.fax && this.siteAccount.fax.replace(/\D/g, ''),
                },
            } as SiteAccountRequestModel;
            const createSiteAccountResponse = await this.siteAccountApi.siteAccountCreateSiteAccount(request as any);
            const siteAccount: EnterpriseCoordinatorSiteAccountUpsearchResponseModel =
                createSiteAccountResponse && createSiteAccountResponse.data && createSiteAccountResponse.data.data;
            return siteAccount;
        } catch {
            return;
        }
    }

    @Action({})
    private async createAdminCredentials(accountId: number) {
        try {
            const request = {
                email: this.siteAdministrator.email,
                firstName: this.siteAdministrator.firstName,
                lastName: this.siteAdministrator.lastName,
                linkedAccount: accountId,
                emailVerified: true,
                resetPasswordEmail: true,
                phoneNumber: `+1${CommonTools.phoneToString(this.siteAdministrator.phone || '')}`
            } as UserRequestModel;
            const createAdminResponse = await this.authenticationApi.userCreatePortalAdmin(request);
            const admin = createAdminResponse && createAdminResponse.data && createAdminResponse.data.data;
            return admin;
        } catch {
            return;
        }
    }

    @Action({})
    async adminAccountExists() {
        try {
            const accountExistsResponse = await this.authenticationApi.userAccountExists(this.siteAdministrator.email);
            const accountExistsData = accountExistsResponse
                && accountExistsResponse.data
                && accountExistsResponse.data.data;
            return { accountExists:  accountExistsData };
        } catch {
            return;
        }
    }
}
